import React from "react";
import { useAuth } from "../../contexts/auth-context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Avatar,
  Grid,
  Button,
  Alert,
  useTheme,
} from "@mui/material";
import {
  User,
  Mail,
  Phone,
  CreditCard,
  Bell,
  BellOff,
  CheckCircle,
  Edit,
} from "lucide-react";
import BillingPortalButton from "../../components/subscription/BillingPortalButton";
import { useNotifications } from "../../hooks/useNotifications";

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSupported, permission, requestPermission, subscribe } = useNotifications();
  const [notificationStatus, setNotificationStatus] = React.useState<'loading' | 'success' | 'error' | null>(null);

  const handleEnableNotifications = async () => {
    setNotificationStatus('loading');

    // Effacer le flag "dismissed" pour que la bannière puisse réapparaître si nécessaire
    localStorage.removeItem('notificationBannerDismissed');

    console.log('[PROFILE] Requesting notification permission');
    const result = await requestPermission();
    console.log('[PROFILE] Permission result:', result);

    if (result === 'granted') {
      // S'abonner aux notifications push
      if (user?.id) {
        try {
          console.log('[PROFILE] Subscribing user to push notifications');
          await subscribe(user.id.toString());
          console.log('[PROFILE] Successfully subscribed');
          setNotificationStatus('success');
        } catch (error) {
          console.error('[PROFILE] Error subscribing:', error);
          setNotificationStatus('error');
        }
      } else {
        setNotificationStatus('success');
      }
    } else {
      setNotificationStatus('error');
    }

    // Reset status after 3 seconds
    setTimeout(() => setNotificationStatus(null), 3000);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "ADMIN":
        return "error";
      case "MODERATOR":
        return "warning";
      case "MODERATOR_FREE":
        return "info";
      case "SAFEE_MODERATOR":
        return "secondary";
      case "GUEST":
        return "default";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "ADMIN":
        return t('profile.roles.admin');
      case "MODERATOR":
        return t('profile.roles.moderator');
      case "MODERATOR_FREE":
        return t('profile.roles.moderatorFree');
      case "SAFEE_MODERATOR":
        return t('profile.roles.safeeModerator');
      case "GUEST":
        return t('profile.roles.guest');
      case "USER":
        return t('profile.roles.user');
      default:
        return role;
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('profile.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {t('profile.title')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Avatar
                src={user.profilePicture || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  border: `3px solid ${theme.palette.primary.main}`,
                  bgcolor: theme.palette.primary.main,
                  fontSize: "3rem",
                }}
              >
                {user.fullname ? user.fullname.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
              </Avatar>

              <Typography variant="h5" gutterBottom>
                {user.fullname || t('profile.user')}
              </Typography>
              <Chip
                label={getRoleLabel(user.role)}
                color={getRoleBadgeColor(user.role)}
                size="small"
              />
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Edit size={16} />}
                  onClick={() => navigate('/profile/edit')}
                >
                  {t('profile.edit.button', 'Modifier')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                {t('profile.personalInfo')}
              </Typography>

              <Stack spacing={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <User size={20} style={{ color: "#666" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.fullName')}
                    </Typography>
                    <Typography variant="body1">
                      {user.fullname || t('profile.notProvided')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Mail size={20} style={{ color: "#666" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.email')}
                    </Typography>
                    <Typography variant="body1">{user.email}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Phone size={20} style={{ color: "#666" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.phone', 'Téléphone')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {user.phone || t('profile.notProvided')}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          {isSupported && (
            <Card elevation={2} sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Bell size={24} style={{ color: "#666" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('profile.notifications.title')}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('profile.notifications.description')}
                </Typography>

                <Stack spacing={2}>
                  {/* Current Status */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {permission === 'granted' ? (
                      <>
                        <CheckCircle size={20} style={{ color: "#4caf50" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#4caf50" }}>
                            {t('profile.notifications.status.enabled')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('profile.notifications.status.enabledDesc')}
                          </Typography>
                        </Box>
                      </>
                    ) : permission === 'denied' ? (
                      <>
                        <BellOff size={20} style={{ color: "#f44336" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#f44336" }}>
                            {t('profile.notifications.status.blocked')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('profile.notifications.status.blockedDesc')}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Bell size={20} style={{ color: "#ff9800" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#ff9800" }}>
                            {t('profile.notifications.status.disabled')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('profile.notifications.status.disabledDesc')}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* Enable Button (only show if not granted) */}
                  {permission !== 'granted' && (
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Bell size={16} />}
                        onClick={handleEnableNotifications}
                        disabled={notificationStatus === 'loading'}
                        sx={{ mt: 1 }}
                      >
                        {notificationStatus === 'loading' ? t('profile.notifications.enabling') : t('profile.notifications.enable')}
                      </Button>
                    </Box>
                  )}

                  {/* Status Messages */}
                  {notificationStatus === 'success' && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {t('profile.notifications.successMessage')}
                    </Alert>
                  )}
                  {notificationStatus === 'error' && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {t('profile.notifications.errorMessage')}
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Ne pas afficher la section Subscription pour MODERATOR sans Stripe (contrat direct) */}
          {!(user.role === 'MODERATOR' && !user.stripeCustomerId) && (
            <Card elevation={2} sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <CreditCard size={24} style={{ color: "#666" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('profile.subscription.title')}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {user.role === 'ADMIN' || user.role === 'MODERATOR_FREE' || user.stripeCustomerId ? (
                    t('profile.subscription.adminDesc')
                  ) : (
                    t('profile.subscription.companyManaged')
                  )}
                </Typography>

                {user.role === 'ADMIN' || user.role === 'MODERATOR_FREE' || user.stripeCustomerId ? (
                  user.stripeCustomerId ? (
                    <BillingPortalButton
                      customerId={user.stripeCustomerId}
                      onError={(error) => console.error("Erreur portail:", error)}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.subscription.noActive')}
                    </Typography>
                  )
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('profile.subscription.managedBy', { role: getRoleLabel(user.role) })}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
